/**
 * StudentTutorConversationOverlays — ce qui s'ouvre par-dessus la conversation : « Copier » (appui
 * long sur un message), « Appeler » ou « Envoyer un SMS » (un numéro touché), la visionneuse d'une photo ou d'un document, le bandeau d'un fichier
 * qui n'a pas pu partir, et l'enregistreur d'un message vocal. Des fenêtres du design system,
 * contrôlées ; l'enregistreur porte son moteur et n'est monté que le temps qu'il est ouvert.
 */
import React from "react"

import { ActionSheet, MediaDisplay, Snackbar } from "../../../../../../design-system"
import { type VoiceFile } from "../../../../../../engine/voice/voiceOutbox"
import { FILE_REFUSED } from "../../../../adapters/student/Messaging/studentConversationAdapter"
import { type PhoneAction } from "./conversationLinks"
import { ConversationPhoneSheet } from "./ConversationPhoneSheet"
import { ConversationVoiceRecorder } from "./ConversationVoiceRecorder"
import { type ConversationViewer } from "./useStudentTutorConversationState"

const TEXTS = {
    copy: "Copier",
}

/** Le bandeau reste le temps que l'ancien fil le gardait. */
const UNSENT_NOTICE_MS = 6000

export type StudentTutorConversationOverlaysProps = {
    copyOpen: boolean
    onCopy: () => void
    onCloseCopy: () => void
    viewer: ConversationViewer | null
    onCloseViewer: () => void
    unsentNotice: boolean
    onHideUnsent: () => void
    /** Le numéro touché dans un message ; `null` : la feuille est fermée. */
    phone: string | null
    onChoosePhone: (action: PhoneAction) => void
    onClosePhone: () => void
    /** L'enregistreur d'un message vocal, ouvert par « Message vocal » ; `null` : fermé. */
    recorder: { threadId: string | null; onClose: () => void; onSend: (file: VoiceFile) => void } | null
}

export function StudentTutorConversationOverlays(props: StudentTutorConversationOverlaysProps) {
    const {
        copyOpen,
        onCopy,
        onCloseCopy,
        viewer,
        onCloseViewer,
        unsentNotice,
        onHideUnsent,
        phone,
        onChoosePhone,
        onClosePhone,
        recorder,
    } = props

    return (
        <>
            <ActionSheet
                visible={copyOpen}
                onClose={onCloseCopy}
                items={[{ icon: "copy", label: TEXTS.copy, onPress: onCopy }]}
            />
            <ConversationPhoneSheet phone={phone} onChoose={onChoosePhone} onClose={onClosePhone} />
            {viewer?.kind === "image" && (
                <MediaDisplay
                    kind="image"
                    visible
                    images={viewer.images}
                    initialIndex={viewer.index}
                    onClose={onCloseViewer}
                />
            )}
            {viewer?.kind === "pdf" && <MediaDisplay kind="pdf" visible uri={viewer.uri} onClose={onCloseViewer} />}
            <Snackbar
                visible={unsentNotice}
                message={FILE_REFUSED}
                tone="danger"
                autoHideMs={UNSENT_NOTICE_MS}
                onDismiss={onHideUnsent}
            />
            {recorder != null && <ConversationVoiceRecorder {...recorder} />}
        </>
    )
}
